/**
 * Service Card Reveal Animation
 *
 * Animates service cards on scroll with a staggered reveal effect.
 * Cards start staggered vertically and animate to aligned positions.
 *
 * Based on Figma design:
 * - Initial state: Cards are staggered vertically (Service Section Reveal - Initial)
 * - Final state: Cards are aligned horizontally (Service Section Reveal - Final)
 *
 * @requires gsap
 * @requires ScrollTrigger
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Initialize service card reveal animations
 */
export function initServiceCardReveal() {
  const containers = document.querySelectorAll("[data-service-reveal]");

  if (!containers.length) return;

  containers.forEach((container) => {
    const cards = container.querySelectorAll("[data-reveal-card]");

    if (!cards.length) return;

    // Create the reveal animation using fromTo so every card (including the first) gets an explicit transform.
    // tl.to() with gsap.set(card, { y: 0 }) for the first card often doesn't apply a transform, so the first card was skipped.
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: "top 80%",
        end: "top 20%",
        scrub: 1,
        // markers: true, // Uncomment for debugging
      },
    });

    const staggerY = 185; // Vertical spacing per card (based on Figma)
    tl.fromTo(
      cards,
      {
        // Use (i + 1) so the first card also has a non-zero initial y and gets a real transform
        y: (i) => (i + 1) * staggerY,
      },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        stagger: {
          each: 0.15,
          ease: "power2.out",
        },
        ease: "power2.out",
      }
    );

    // Add subtle scale effect on individual cards when they enter viewport
    cards.forEach((card) => {
      ScrollTrigger.create({
        trigger: card,
        start: "top 90%",
        onEnter: () => {
          gsap.to(card.querySelector(".service-card"), {
            scale: 1,
            duration: 0.5,
            ease: "back.out(1.2)",
          });
        },
        once: true,
      });

      // Set initial scale
      gsap.set(card.querySelector(".service-card"), {
        scale: 0.95,
      });
    });
  });
}

/**
 * Cleanup function to destroy ScrollTriggers
 */
export function destroyServiceCardReveal() {
  ScrollTrigger.getAll().forEach((trigger) => {
    if (trigger.vars.trigger?.closest("[data-service-reveal]")) {
      trigger.kill();
    }
  });
}

// Auto-initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initServiceCardReveal);
} else {
  initServiceCardReveal();
}
